'use client';

import clsx from 'clsx';
import { type PropsWithChildren, useId, useState } from 'react';
import { BASE_PATH } from '@/constants/constants';
import { ContentLoader, RectangleLoader } from '@/components/ContentLoader/ContentLoader';
import { Alert } from '@/components/Alert/Alert';
import './blocks/AccordionItem.scss';

export type AccordionItemProps = PropsWithChildren<{
	className?: string;
	error?: React.ReactNode;
	expandedInitially?: boolean;
	hideBorder?: boolean;
	isLoading?: boolean;
	onExpandedChange?: ({ isExpanded, title }: { isExpanded: boolean; title: string | React.ReactNode }) => void;
	title: string | React.ReactNode;
}>;

export function AccordionItem({
	children,
	className = '[ py-sm ]',
	error,
	expandedInitially = false,
	hideBorder = false,
	isLoading = false,
	onExpandedChange,
	title
}: AccordionItemProps) {
	const [expanded, setExpanded] = useState(expandedInitially);
	const id = useId();
	return (
		<div className={clsx('[ accordion-item ]', className)} data-hide-border={hideBorder}>
			<button
				aria-expanded={expanded}
				aria-controls={`accordion-section-${id}`}
				className="[ accordion-item__trigger ] [ color-text-primary d-flex p-none w-full ]"
				data-loading={isLoading}
				disabled={isLoading}
				id={`accordion-item-${id}`}
				onClick={() => {
					const isExpanded = !expanded;
					setExpanded(isExpanded);
					if (onExpandedChange) {
						onExpandedChange({ isExpanded, title });
					}
				}}
				type="button"
			>
				<span className="[ accordion-item__title ] [ d-flex items-center justify-between gap-md w-full text-start ]">
					{isLoading ? (
						<ContentLoader fullWidth height={24}>
							<RectangleLoader />
						</ContentLoader>
					) : (
						<>
							{typeof title === 'string' ? (
								<h3 className="text-list-heading w-fit-content color-text-primary">{title}</h3>
							) : (
								title
							)}
						</>
					)}
					<svg className="icon accordion-item__icon" fill="currentColor">
						<use xlinkHref={`${BASE_PATH}/speedboat/icons/toyota-icons.svg#chevron-down`} />
					</svg>
				</span>
			</button>
			<section
				id={`accordion-section-${id}`}
				className="[ accordion-item__content ] [ color-text-secondary ]"
				aria-labelledby={`accordion-item-${id}`}
				aria-hidden={!expanded}
			>
				<div className="accordion-item__content-wrapper">
					{error ? (
						<Alert classname="mt-sm" message={error} variant="warning" />
					) : (
						<div className="pt-sm text-body-base">{children}</div>
					)}
				</div>
			</section>
		</div>
	);
}
